import type { UVInsertFaceElementTextListConfig } from "../../models/uv-inserts"

/**
 * The geometry of a `TEXT_LIST`'s optional vertical rule, in face px.
 *
 * `reservedWidth` is the horizontal strip the rule occupies **inside** the element box, on the
 * anchor side: the DOM pads the `<ul>` by it and the canvas shifts its text column by it, so the
 * rule always has room and a moment wraps the same way in both renderers.
 */
export type UvInsertTextListRuleMetrics = {
  /** `gap + stripWidth` — what the text column gives up on the anchor side. */
  reservedWidth: number
  /** Width of the rule itself: the wider of the line and a dot. */
  stripWidth: number
  /** Px the rule runs past the first and last row, at both ends. */
  overhang: number
  /** Line thickness in px. */
  lineWidth: number
  /** Dot radius in px; `0` means no end dots. */
  dotRadius: number
  /** Resolved colour, or `undefined` when neither the rule nor the element declares one. */
  color: string | undefined
}

export type GetUvInsertTextListRuleMetricsOptions = {
  element: UVInsertFaceElementTextListConfig
}

/**
 * The rule metrics for a `TEXT_LIST` element, or `null` when it declares no rule.
 *
 * One builder for both renderers, mirroring {@link getUvInsertTextListRows}: the DOM list and the
 * canvas cart preview each had their own copy of the row logic once and drifted, and the rule has
 * the same failure mode — a line the customer sees on the face but not in the cart preview.
 *
 * The rule is drawn from the rows actually rendered, so it flexes: nothing here knows the row
 * count, and neither renderer may hardcode a length.
 *
 * @param options - Options object.
 * @param options.element - The parsed `TEXT_LIST` element config.
 * @returns The rule geometry, or `null` for an element that does not opt in.
 */
export function getUvInsertTextListRuleMetrics({
  element,
}: GetUvInsertTextListRuleMetricsOptions): UvInsertTextListRuleMetrics | null {
  const { rule } = element
  if (!rule) return null

  // The dots are usually wider than the hairline, but a thick rule with tiny dots is legal; take
  // whichever actually needs the room so the text never runs under the rule.
  const stripWidth = Math.max(rule.width, 2 * rule.dotRadius)

  return {
    reservedWidth: rule.gap + stripWidth,
    stripWidth,
    overhang: rule.overhang,
    lineWidth: rule.width,
    dotRadius: rule.dotRadius,
    color: rule.color ?? element.color,
  }
}
